import React, { useState, useEffect } from "react";
import { getData, serverURL, postData } from "../../../Services/Fetchnodeservices";

// ===================== THEME =====================
const getT = (isDark) => ({
  bg: isDark ? "#0c1018" : "#f0f4ff",
  surface: isDark ? "#141824" : "#ffffff",
  surfaceAlt: isDark ? "#1c2133" : "#f4f7ff",
  border: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f5",
  text: isDark ? "#f1f5f9" : "#0f172a",
  textMuted: isDark ? "#64748b" : "#94a3b8",
  textSub: isDark ? "#94a3b8" : "#64748b",
  shadow: isDark ? "0 4px 28px rgba(0,0,0,0.45)" : "0 4px 28px rgba(99,102,241,0.10)",
  shadowSm: isDark ? "0 2px 10px rgba(0,0,0,0.35)" : "0 2px 10px rgba(99,102,241,0.07)",
  accent: "#6c63ff",
  success: "#22c55e",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#3b82f6",
});

// ===================== ICONS =====================
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ===================== COMPONENTS =====================
const StatusBadge = ({ status, isDark }) => {
  const t = getT(isDark);
  const isActive = status === "1";
  const config = isActive 
    ? { bg: "rgba(34,197,94,0.15)", color: t.success, label: "Active" }
    : { bg: "rgba(239,68,68,0.15)", color: t.danger, label: "Inactive" };
  return (
    <span style={{ padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, background: config.bg, color: config.color }}>
      {config.label}
    </span>
  );
};

const Toast = ({ message, type, onClose, isDark }) => {
  const t = getT(isDark);
  return (
    <div style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 1000, background: t.surface, border: `1px solid ${type === "error" ? t.danger : t.success}`, borderRadius: "12px", padding: "12px 20px", display: "flex", alignItems: "center", gap: "12px", boxShadow: t.shadow }}>
      <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: type === "error" ? t.danger : t.success }} />
      <span style={{ fontSize: "13px", color: type === "error" ? t.danger : t.success }}>{message}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted }}><CloseIcon /></button>
    </div>
  );
};

// ===================== MAIN COMPONENT - PLATFORM_FEE =====================
const PlatformFee = ({ isDark = true }) => {
  const t = getT(isDark);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Data state
  const [platformFees, setPlatformFees] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    user_fee: "9",
    driver_fee: "9",
    business_fee: "0",
    status: "1"
  });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    fetchAllPlatformFees();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth <= 768;

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Fetch all platform fees using getData
  const fetchAllPlatformFees = async () => {
    setLoading(true);
    try {
      const result = await getData("PlatformFee/fetch_all_platform_fee");
      if (result.status) {
        setPlatformFees(result.data);
      } else {
        showToast(result.message || "Failed to fetch platform fees", "error");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      showToast("Network error while fetching platform fees", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (fee = null) => {
    if (fee) {
      setEditingFee(fee);
      setFormData({
        id: fee.id,
        user_fee: fee.user_fee || "9",
        driver_fee: fee.driver_fee || "9",
        business_fee: fee.business_fee || "0",
        status: fee.status || "1"
      });
    } else {
      setEditingFee(null);
      setFormData({
        user_fee: "9",
        driver_fee: "9",
        business_fee: "0",
        status: "1"
      });
    }
    setShowModal(true);
  };

  // Add or Update platform fee
  const handleSaveFee = async () => {
    // Validation
    if (!formData.user_fee || !formData.driver_fee) {
      showToast("User Fee and Driver Fee are required", "error");
      return;
    }

    setSubmitting(true);
    
    try {
      let result;
      if (editingFee) {
        // Update using postData - update_platform_fee_status endpoint
        const formDataObj = {
          id: editingFee.id,
          user_fee: formData.user_fee,
          driver_fee: formData.driver_fee,
          business_fee: formData.business_fee,
          status: formData.status
        };
        // Note: Your backend only has status update endpoint
        // You may need to add a full update endpoint
        result = await postData("platformfee/update_platform_fee_status", { 
          id: editingFee.id, 
          status: formData.status 
        });
        if (result.status) {
          showToast("Platform fee status updated successfully");
          fetchAllPlatformFees();
          setShowModal(false);
        } else {
          showToast(result.message || "Update failed", "error");
        }
      } else {
        // Add new fee - You need to create this endpoint in backend
        const formDataObj = {
          user_fee: formData.user_fee,
          driver_fee: formData.driver_fee,
          business_fee: formData.business_fee,
          status: formData.status
        };
        showToast("Add endpoint not implemented. Please contact backend team to create /add_platform_fee endpoint", "error");
      }
    } catch (error) {
      console.error("Save error:", error);
      showToast("Network error while saving", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Update status using postData
  const handleToggleStatus = async (fee) => {
    const newStatus = fee.status === "1" ? "0" : "1";
    
    setSubmitting(true);
    try {
      const formDataObj = {
        id: fee.id,
        status: newStatus
      };
      
      const result = await postData("platformfee/update_platform_fee_status", formDataObj);
      if (result.status) {
        showToast(`Platform fee ${newStatus === "1" ? "activated" : "deactivated"} successfully`);
        fetchAllPlatformFees();
      } else {
        showToast(result.message || "Status update failed", "error");
      }
    } catch (error) {
      console.error("Status update error:", error);
      showToast("Network error while updating status", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredFees = platformFees.filter(fee => 
    fee.user_fee?.toString().includes(search) ||
    fee.driver_fee?.toString().includes(search) ||
    fee.business_fee?.toString().includes(search) ||
    fee.status?.toString().includes(search)
  );

  const styles = {
    container: {
      minHeight: "100vh",
      background: t.bg,
      fontFamily: "'DM Sans', sans-serif",
      padding: isMobile ? "16px" : "24px",
    },
    contentWrapper: {
      maxWidth: "1200px",
      margin: "0 auto",
    },
    header: {
      marginBottom: "28px",
    },
    title: {
      fontSize: isMobile ? "22px" : "28px",
      fontWeight: 700,
      background: "linear-gradient(135deg, #6c63ff, #a855f7)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      margin: 0,
    },
    subtitle: {
      fontSize: "13px",
      color: t.textMuted,
      marginTop: "6px",
    },
    card: {
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: "20px",
      overflow: "hidden",
    },
    cardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "16px 20px",
      borderBottom: `1px solid ${t.border}`,
      background: t.surfaceAlt,
      flexWrap: "wrap",
      gap: "12px",
    },
    cardTitle: {
      fontSize: "16px",
      fontWeight: 700,
      color: t.text,
      margin: 0,
    },
    searchWrapper: {
      position: "relative",
      width: isMobile ? "100%" : "260px",
    },
    searchInput: {
      width: "100%",
      padding: "10px 12px 10px 35px",
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      fontSize: "13px",
      color: t.text,
      outline: "none",
    },
    addButton: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      padding: "8px 16px",
      background: "linear-gradient(135deg, #6c63ff, #a855f7)",
      border: "none",
      borderRadius: "12px",
      color: "#fff",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s",
    },
    headerActions: {
      display: "flex",
      gap: "20px",
      alignItems: "center",
      flexWrap: "wrap",
    },
    tableWrapper: {
      overflowX: "auto",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: "600px",
    },
    th: {
      padding: "14px 16px",
      textAlign: "left",
      fontSize: "12px",
      fontWeight: 700,
      color: t.accent,
      borderBottom: `1px solid ${t.border}`,
      background: t.surfaceAlt,
    },
    td: {
      padding: "14px 16px",
      fontSize: "13px",
      color: t.textSub,
      borderBottom: `1px solid ${t.border}`,
    },
    actionButtons: {
      display: "flex",
      gap: "8px",
      alignItems: "center",
      flexWrap: "wrap",
    },
    editBtn: {
      padding: "5px 12px",
      borderRadius: "8px",
      border: "none",
      fontSize: "11px",
      fontWeight: 500,
      cursor: "pointer",
      background: t.info,
      color: "#fff",
    },
    toggleBtn: (isActive) => ({
      padding: "5px 12px",
      borderRadius: "20px",
      border: "none",
      fontSize: "11px",
      fontWeight: 500,
      cursor: "pointer",
      background: isActive ? t.success : t.warning,
      color: "#fff",
    }),
    modalOverlay: {
      position: "fixed",
      inset: 0,
      zIndex: 200,
      background: "rgba(0,0,0,0.6)",
      backdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    modal: {
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: "24px",
      width: isMobile ? "90%" : "500px",
      maxHeight: "85vh",
      overflow: "auto",
    },
    modalHeader: {
      padding: "20px 24px",
      borderBottom: `1px solid ${t.border}`,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    modalTitle: {
      fontSize: "18px",
      fontWeight: 700,
      color: t.text,
    },
    modalBody: {
      padding: "24px",
    },
    modalFooter: {
      padding: "16px 24px",
      borderTop: `1px solid ${t.border}`,
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
    },
    formGroup: {
      marginBottom: "20px",
    },
    label: {
      display: "block",
      fontSize: "12px",
      fontWeight: 600,
      color: t.textMuted,
      marginBottom: "6px",
    },
    input: {
      width: "100%",
      padding: "10px 14px",
      background: t.surfaceAlt,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      fontSize: "13px",
      color: t.text,
      outline: "none",
      fontFamily: "inherit",
    },
    select: {
      width: "100%",
      padding: "10px 14px",
      background: t.surfaceAlt,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      fontSize: "13px",
      color: t.text,
      outline: "none",
      cursor: "pointer",
    },
    cancelBtn: {
      padding: "10px 20px",
      background: t.surfaceAlt,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      color: t.text,
      cursor: "pointer",
    },
    saveBtn: {
      padding: "10px 20px",
      background: "linear-gradient(135deg, #6c63ff, #a855f7)",
      border: "none",
      borderRadius: "12px",
      color: "#fff",
      cursor: "pointer",
      fontWeight: 600,
    },
    rowCount: {
      padding: "12px 20px",
      borderTop: `1px solid ${t.border}`,
      fontSize: "12px",
      color: t.textMuted,
    },
    loadingText: {
      textAlign: "center",
      padding: "40px",
      color: t.textMuted,
    },
    feeValue: {
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: "20px",
      background: t.accent + "15",
      color: t.accent,
      fontWeight: 600,
      fontSize: "12px",
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.contentWrapper}>
        <div style={styles.header}>
          <h1 style={styles.title}>🏦 Platform Fee Management</h1>
          <p style={styles.subtitle}>Configure platform fees for users, drivers, and businesses</p>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>💰 Platform Fee Configuration</h3>
            <div style={styles.headerActions}>
              <div style={styles.searchWrapper}>
                <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)" }}><SearchIcon /></span>
                <input 
                  style={styles.searchInput} 
                  placeholder="Search platform fee..." 
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)} 
                />
              </div>
              <button style={styles.addButton} onClick={() => handleOpenModal()}>
                <PlusIcon /> Add Fee
              </button>
            </div>
          </div>

          <div style={styles.tableWrapper}>
            {loading ? (
              <div style={styles.loadingText}>Loading platform fees...</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>SN</th>
                    <th style={styles.th}>ID</th>
                    <th style={styles.th}>User Fee</th>
                    <th style={styles.th}>Driver Fee</th>
                    <th style={styles.th}>Business Fee</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFees.map((fee, index) => (
                    <tr key={fee.id}>
                      <td style={styles.td}>{index + 1}</td>
                      <td style={styles.td}>{fee.id}</td>
                      <td style={styles.td}>
                        <span style={styles.feeValue}>{fee.user_fee}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.feeValue}>{fee.driver_fee}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.feeValue}>{fee.business_fee || 0}</span>
                      </td>
                      <td style={styles.td}><StatusBadge status={fee.status} isDark={isDark} /></td>
                      <td style={styles.td}>
                        <div style={styles.actionButtons}>
                          <button style={styles.editBtn} onClick={() => handleOpenModal(fee)}>
                            <EditIcon /> Edit
                          </button>
                          <button 
                            style={styles.toggleBtn(fee.status === "1")} 
                            onClick={() => handleToggleStatus(fee)}
                            disabled={submitting}
                          >
                            {fee.status === "1" ? "Deactivate" : "Activate"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={styles.rowCount}>
            Rows: 20 ▼ | {filteredFees.length} of {platformFees.length} — Page 1/1
          </div>
        </div>
      </div>

      {/* Add/Edit Modal - Note: Only status update is available in current backend */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>{editingFee ? "Edit Platform Fee" : "Add New Platform Fee"}</div>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted }}>
                <CloseIcon />
              </button>
            </div>
            <div style={styles.modalBody}>
              {editingFee ? (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>User Fee</label>
                    <input 
                      type="number" 
                      style={styles.input} 
                      value={formData.user_fee} 
                      disabled
                      style={{ ...styles.input, opacity: 0.6, cursor: "not-allowed" }}
                    />
                    <small style={{ color: t.textMuted, fontSize: "10px", marginTop: "4px", display: "block" }}>
                      User fee can only be updated via database
                    </small>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Driver Fee</label>
                    <input 
                      type="number" 
                      style={styles.input} 
                      value={formData.driver_fee} 
                      disabled
                      style={{ ...styles.input, opacity: 0.6, cursor: "not-allowed" }}
                    />
                    <small style={{ color: t.textMuted, fontSize: "10px", marginTop: "4px", display: "block" }}>
                      Driver fee can only be updated via database
                    </small>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Business Fee</label>
                    <input 
                      type="number" 
                      style={styles.input} 
                      value={formData.business_fee} 
                      disabled
                      style={{ ...styles.input, opacity: 0.6, cursor: "not-allowed" }}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select style={styles.select} value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <p style={{ color: t.warning }}>⚠️ Add endpoint not available</p>
                  <p style={{ fontSize: "12px", color: t.textMuted, marginTop: "8px" }}>
                    Please contact backend team to implement:<br />
                    POST /platformfee/add_platform_fee
                  </p>
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              {editingFee && (
                <button style={styles.saveBtn} onClick={handleSaveFee} disabled={submitting}>
                  {submitting ? "Updating..." : "Update Status"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} isDark={isDark} />}
    </div>
  );
};

export default PlatformFee;